import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Sidebar } from "primereact/sidebar";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";

import { toast } from "react-toastify";
import {
  GrPrevious,
  GrNext,
} from "react-icons/gr";

import {
  TbEyeSearch,
  TbUserEdit,
  TbPasswordUser,
  TbTrash,
  TbCrown,
  TbShieldCheck,
} from "react-icons/tb";

import api from "../api/axios";
import "../assets/styles/page.css";
import "../assets/styles/directory-tables.css";

const PAGE_SIZE = 10;
const PAGE_NAME = "Users";

const emptyAdminForm = {
  name: "",
  email: "",
  password: "",
  role_id: "",
  status: "active",
  image: null,
};

const emptyPasswordForm = {
  password: "",
  confirmPassword: "",
};

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [...new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  return pages.flatMap((page, index) =>
    index > 0 && page - pages[index - 1] > 1
      ? [`ellipsis-${page}`, page]
      : [page]
  );
};

const Users = () => {
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] =
    useState("");
  const [status, setStatus] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] =
    useState(false);
  const [saving, setSaving] = useState(false);
  const [
    changingPassword,
    setChangingPassword,
  ] = useState(false);

  const [
    formSidebarOpen,
    setFormSidebarOpen,
  ] = useState(false);

  const [
    viewSidebarOpen,
    setViewSidebarOpen,
  ] = useState(false);

  const [
    passwordSidebarOpen,
    setPasswordSidebarOpen,
  ] = useState(false);

  const [
    editingAdminId,
    setEditingAdminId,
  ] = useState(null);

  const [
    selectedAdmin,
    setSelectedAdmin,
  ] = useState(null);

  const [adminForm, setAdminForm] = useState(
    emptyAdminForm
  );

  const [passwordForm, setPasswordForm] =
    useState(emptyPasswordForm);

  const [imagePreview, setImagePreview] =
    useState("");

  const isEdit = Boolean(editingAdminId);

  const activeAdmins = useMemo(
    () =>
      admins.filter(
        (admin) => admin.status === "active"
      ).length,
    [admins]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);

      const response = await api.get(
        "/admin/users/admins/roles",
        {
          headers: getPermissionHeaders("view"),
        }
      );

      setRoles(response.data?.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to fetch admin roles."
      );
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchAdmins = async (page = 1) => {
    try {
      setLoading(true);

      const response = await api.get(
        "/admin/users/admins",
        {
          headers: getPermissionHeaders("view"),

          params: {
            page,
            limit: PAGE_SIZE,
            search:
              debouncedSearch || undefined,
            status: status || undefined,
            role_id:
              roleFilter || undefined,
          },
        }
      );

      setAdmins(response.data?.data || []);

      setPagination(
        response.data?.pagination || {
          page,
          total: 0,
          totalPages: 1,
        }
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to fetch admins."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchAdmins(1);
  }, [
    debouncedSearch,
    status,
    roleFilter,
  ]);

  const resetAdminForm = () => {
    setEditingAdminId(null);
    setSelectedAdmin(null);
    setAdminForm(emptyAdminForm);
    setImagePreview("");
  };

  const openCreateAdmin = () => {
    resetAdminForm();
    setFormSidebarOpen(true);
  };

  const openEditAdmin = (admin) => {
    setEditingAdminId(admin.user_id);
    setSelectedAdmin(admin);

    setAdminForm({
      name: admin.name || "",
      email: admin.email || "",
      password: "",
      role_id:
        admin.role_id ||
        admin.role_details?.role_id ||
        "",
      status: admin.status || "active",
      image: null,
    });

    setImagePreview(admin.image_url || "");
    setFormSidebarOpen(true);
  };

  const openViewAdmin = async (admin) => {
    try {
      const response = await api.get(
        `/admin/users/admins/${admin.user_id}`,
        {
          headers: getPermissionHeaders(
            "view"
          ),
        }
      );

      setSelectedAdmin(
        response.data?.data || admin
      );
    } catch (error) {
      setSelectedAdmin(admin);

      toast.error(
        error.response?.data?.message ||
          "Failed to fetch admin details."
      );
    }

    setViewSidebarOpen(true);
  };

  const openPasswordSidebar = (admin) => {
    setSelectedAdmin(admin);
    setPasswordForm(emptyPasswordForm);
    setPasswordSidebarOpen(true);
  };

  const closeFormSidebar = () => {
    setFormSidebarOpen(false);
    resetAdminForm();
  };

  const handleImageChange = (event) => {
    const file =
      event.target.files?.[0] || null;

    setAdminForm((previous) => ({
      ...previous,
      image: file,
    }));

    if (file) {
      setImagePreview(
        URL.createObjectURL(file)
      );
    } else {
      setImagePreview(
        selectedAdmin?.image_url || ""
      );
    }
  };

  const submitAdmin = async (event) => {
    event.preventDefault();

    const cleanName =
      adminForm.name.trim();

    const cleanEmail =
      adminForm.email.trim();

    if (!cleanName || !cleanEmail) {
      toast.error(
        "Name and email are required."
      );
      return;
    }

    if (!adminForm.role_id) {
      toast.error(
        "Please select an admin role."
      );
      return;
    }

    if (
      !isEdit &&
      !adminForm.password.trim()
    ) {
      toast.error(
        "Password is required for a new admin."
      );
      return;
    }

    if (
      !isEdit &&
      adminForm.password.length < 6
    ) {
      toast.error(
        "Password must be at least 6 characters."
      );
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", cleanName);
      formData.append("email", cleanEmail);
      formData.append(
        "status",
        adminForm.status
      );

      formData.append(
        "role_id",
        String(adminForm.role_id)
      );

      if (adminForm.password) {
        formData.append(
          "password",
          adminForm.password
        );
      }

      if (adminForm.image) {
        formData.append(
          "image",
          adminForm.image
        );
      }

      if (isEdit) {
        await api.put(
          `/admin/users/admins/${editingAdminId}`,
          formData,
          {
            headers:
              getPermissionHeaders("edit"),
          }
        );

        toast.success(
          "Admin updated successfully."
        );
      } else {
        await api.post(
          "/admin/users/admins",
          formData,
          {
            headers:
              getPermissionHeaders("add"),
          }
        );

        toast.success(
          "Admin created successfully."
        );
      }

      closeFormSidebar();

      await fetchAdmins(
        isEdit ? pagination.page : 1
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Admin save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  const submitPasswordChange = async (
    event
  ) => {
    event.preventDefault();

    if (!selectedAdmin) return;

    if (
      !passwordForm.password ||
      passwordForm.password.length < 6
    ) {
      toast.error(
        "Password must be at least 6 characters."
      );
      return;
    }

    if (
      passwordForm.password !==
      passwordForm.confirmPassword
    ) {
      toast.error(
        "Passwords do not match."
      );
      return;
    }

    try {
      setChangingPassword(true);

      await api.patch(
        `/admin/users/admins/${selectedAdmin.user_id}/password`,
        {
          password:
            passwordForm.password,
        },
        {
          headers:
            getPermissionHeaders("edit"),
        }
      );

      toast.success(
        "Password changed successfully."
      );

      setPasswordSidebarOpen(false);
      setPasswordForm(emptyPasswordForm);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Password update failed."
      );
    } finally {
      setChangingPassword(false);
    }
  };

  const confirmStatusChange = (
    admin,
    newStatus
  ) => {
    if (admin.is_super_admin) {
      toast.error(
        "The Super Admin status cannot be changed."
      );
      return;
    }

    confirmDialog({
      message: `Change ${admin.name}'s status to "${newStatus}"?`,
      header: "Update Admin Status",
      icon: "pi pi-exclamation-triangle",

      accept: async () => {
        try {
          const formData = new FormData();

          formData.append(
            "status",
            newStatus
          );

          await api.put(
            `/admin/users/admins/${admin.user_id}`,
            formData,
            {
              headers:
                getPermissionHeaders("edit"),
            }
          );

          toast.success(
            "Admin status updated."
          );

          await fetchAdmins(
            pagination.page
          );
        } catch (error) {
          toast.error(
            error.response?.data?.message ||
              "Status update failed."
          );
        }
      },
    });
  };

  const confirmDeleteAdmin = (admin) => {
    if (
      admin.is_super_admin ||
      admin.can_delete === false
    ) {
      toast.error(
        "The Super Admin account cannot be deleted."
      );
      return;
    }

    confirmDialog({
      message: `Delete ${admin.name}'s admin account? This action cannot be undone.`,
      header: "Delete Admin",
      icon: "pi pi-exclamation-triangle",

      acceptClassName: "p-button-danger",

      accept: async () => {
        try {
          await api.delete(
            `/admin/users/admins/${admin.user_id}`,
            {
              headers:
                getPermissionHeaders(
                  "delete"
                ),
            }
          );

          toast.success(
            "Admin deleted successfully."
          );

          const nextPage =
            admins.length === 1 &&
            pagination.page > 1
              ? pagination.page - 1
              : pagination.page;

          await fetchAdmins(nextPage);
        } catch (error) {
          toast.error(
            error.response?.data?.message ||
              "Failed to delete admin."
          );
        }
      },
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  const getRoleName = (admin) =>
    admin.role_details?.role_name ||
    "No role assigned";

  return (
    <section className="admin-page users-page premium-users-page">
      <ConfirmDialog />

      <div className="page-header users-hero-header">
        <div>
          <p>Admin Management</p>
          <h1>Users</h1>
        </div>

        <button
          type="button"
          className="primary-action"
          onClick={openCreateAdmin}
        >
          + Add Admin
        </button>
      </div>

      <div className="page-card premium-user-card">
        <div className="premium-user-toolbar">
          <div>
            <h2>Admin Directory</h2>

            <p>
              {loading
                ? "Loading admins..."
                : `${pagination.total} admin(s) found. ${activeAdmins} active on this page.`}
            </p>
          </div>

          <div className="premium-user-filters">
            <input
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search name or email..."
            />

            <select
              value={roleFilter}
              onChange={(event) =>
                setRoleFilter(
                  event.target.value
                )
              }
              disabled={rolesLoading}
            >
              <option value="">
                All roles
              </option>

              {roles.map((role) => (
                <option
                  key={role.role_id}
                  value={role.role_id}
                >
                  {role.role_name}
                </option>
              ))}
            </select>

            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value)
              }
            >
              <option value="">
                All statuses
              </option>
              <option value="active">
                Active
              </option>
              <option value="inactive">
                Inactive
              </option>
              <option value="pending">
                Pending
              </option>
              <option value="blocked">
                Blocked
              </option>
            </select>
          </div>
        </div>

        <div className={`directory-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="directory-data-table admin-directory-table">
            <thead>
              <tr>
                <th>Administrator</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last updated</th>
                <th aria-label="Actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && admins.length === 0 && (
                <tr>
                  <td className="directory-table-empty" colSpan="6">
                    <TbShieldCheck />
                    <strong>No admins found</strong>
                    <span>Create a new admin or adjust your filters.</span>
                  </td>
                </tr>
              )}

              {admins.map((admin) => (
                <tr
                  className={admin.is_super_admin ? "super-admin-row" : ""}
                  key={admin.user_id}
                >
                  <td className="directory-person-cell">
                    <div className="directory-person">
                      {admin.image_url ? (
                        <img src={admin.image_url} alt="" />
                      ) : (
                        <span className="directory-avatar">
                          {admin.name?.charAt(0)?.toUpperCase() || "A"}
                        </span>
                      )}

                      <div>
                        <div className="directory-name-line">
                          <strong>{admin.name}</strong>
                          {admin.is_super_admin && (
                            <span className="super-admin-label">
                              <TbCrown /> Super Admin
                            </span>
                          )}
                        </div>
                        <span>{admin.email}</span>
                        <small>Admin #{admin.user_id}</small>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="directory-role">
                      <TbShieldCheck />
                      {getRoleName(admin)}
                    </span>
                  </td>

                  <td>
                    <select
                      className={`directory-status-select status-${admin.status}`}
                      value={admin.status}
                      disabled={admin.is_super_admin}
                      aria-label={`Change ${admin.name}'s status`}
                      onChange={(event) =>
                        confirmStatusChange(admin, event.target.value)
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </td>

                  <td className="directory-date-cell">
                    <strong>{formatDate(admin.createdAt)}</strong>
                    <span>Account created</span>
                  </td>

                  <td className="directory-date-cell">
                    <strong>{formatDate(admin.updatedAt)}</strong>
                    <span>Profile update</span>
                  </td>

                  <td className="directory-actions-cell">
                    <div className="directory-row-actions">
                      <button
                        type="button"
                        title="View admin"
                        aria-label={`View ${admin.name}`}
                        onClick={() => openViewAdmin(admin)}
                      >
                        <TbEyeSearch />
                      </button>
                      <button
                        type="button"
                        title="Edit admin"
                        aria-label={`Edit ${admin.name}`}
                        onClick={() => openEditAdmin(admin)}
                      >
                        <TbUserEdit />
                      </button>
                      <button
                        type="button"
                        title="Change password"
                        aria-label={`Change ${admin.name}'s password`}
                        onClick={() => openPasswordSidebar(admin)}
                      >
                        <TbPasswordUser />
                      </button>
                      {!admin.is_super_admin && admin.can_delete !== false && (
                        <button
                          type="button"
                          className="danger"
                          title="Delete admin"
                          aria-label={`Delete ${admin.name}`}
                          onClick={() => confirmDeleteAdmin(admin)}
                        >
                          <TbTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination">
          <button
            type="button"
            className="directory-pagination-direction"
            disabled={
              pagination.page <= 1 ||
              loading
            }
            onClick={() =>
              fetchAdmins(
                pagination.page - 1
              )
            }
          >
            <GrPrevious /> Previous
          </button>

          <div className="directory-pagination-pages">
            {getPaginationItems(pagination.page, pagination.totalPages).map(
              (item) =>
                typeof item === "string" ? (
                  <span key={item}>…</span>
                ) : (
                  <button
                    type="button"
                    className={item === pagination.page ? "active" : ""}
                    key={item}
                    disabled={loading}
                    aria-label={`Go to page ${item}`}
                    aria-current={item === pagination.page ? "page" : undefined}
                    onClick={() => fetchAdmins(item)}
                  >
                    {item}
                  </button>
                )
            )}
          </div>

          <button
            type="button"
            className="directory-pagination-direction"
            disabled={
              pagination.page >=
                pagination.totalPages ||
              loading
            }
            onClick={() =>
              fetchAdmins(
                pagination.page + 1
              )
            }
          >
            Next <GrNext />
          </button>
        </div>
      </div>

      {/* VIEW ADMIN */}

      <Sidebar
        visible={viewSidebarOpen}
        position="right"
        onHide={() =>
          setViewSidebarOpen(false)
        }
        className="premium-user-sidebar"
      >
        {selectedAdmin && (
          <div className="user-view-shell">
            <div className="user-view-hero">
              {selectedAdmin.image_url ? (
                <img
                  src={
                    selectedAdmin.image_url
                  }
                  alt={selectedAdmin.name}
                />
              ) : (
                <div>
                  {selectedAdmin.name
                    ?.charAt(0)
                    ?.toUpperCase() || "A"}
                </div>
              )}
            </div>

            <div className="user-view-title">
              <span>
                {selectedAdmin
                  .is_super_admin
                  ? "Super Administrator"
                  : "Administrator"}
              </span>

              <h2>
                {selectedAdmin.name}
              </h2>

              <p>
                {selectedAdmin.email}
              </p>
            </div>

            <div className="user-view-meta">
              <div>
                <strong>User ID</strong>
                <span>
                  {selectedAdmin.user_id}
                </span>
              </div>

              <div>
                <strong>Role</strong>
                <span>
                  {getRoleName(
                    selectedAdmin
                  )}
                </span>
              </div>

              <div>
                <strong>Status</strong>

                <span
                  className={`status-badge ${selectedAdmin.status}`}
                >
                  {selectedAdmin.status}
                </span>
              </div>

              <div>
                <strong>Created</strong>
                <span>
                  {formatDate(
                    selectedAdmin.createdAt
                  )}
                </span>
              </div>

              <div>
                <strong>Updated</strong>
                <span>
                  {formatDate(
                    selectedAdmin.updatedAt
                  )}
                </span>
              </div>
            </div>

            <div className="user-view-actions">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() =>
                  openPasswordSidebar(
                    selectedAdmin
                  )
                }
              >
                Change Password
              </button>

              <button
                type="button"
                className="primary-action"
                onClick={() => {
                  setViewSidebarOpen(false);
                  openEditAdmin(
                    selectedAdmin
                  );
                }}
              >
                Edit Admin
              </button>
            </div>
          </div>
        )}
      </Sidebar>

      {/* CREATE / EDIT ADMIN */}

      <Sidebar
        visible={formSidebarOpen}
        position="right"
        onHide={closeFormSidebar}
        className="premium-user-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>Admin Setup</p>

            <h2>
              {isEdit
                ? "Edit Admin"
                : "Add Admin"}
            </h2>
          </div>

          <form
            className="premium-sidebar-form"
            onSubmit={submitAdmin}
          >
            <div className="profile-upload-preview premium-profile-preview">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Admin preview"
                />
              ) : (
                <div>
                  {adminForm.name
                    ?.charAt(0)
                    ?.toUpperCase() || "A"}
                </div>
              )}
            </div>

            <label>
              Profile Image

              <input
                type="file"
                style={{
                  padding: "14px",
                }}
                accept="image/*"
                onChange={
                  handleImageChange
                }
              />
            </label>

            <label>
              Name

              <input
                value={adminForm.name}
                onChange={(event) =>
                  setAdminForm(
                    (previous) => ({
                      ...previous,
                      name: event.target
                        .value,
                    })
                  )
                }
                placeholder="Admin name"
                required
              />
            </label>

            <label>
              Email

              <input
                type="email"
                value={adminForm.email}
                onChange={(event) =>
                  setAdminForm(
                    (previous) => ({
                      ...previous,
                      email:
                        event.target.value,
                    })
                  )
                }
                placeholder="admin@example.com"
                required
              />
            </label>

            {!isEdit && (
              <label>
                Password

                <input
                  type="password"
                  value={
                    adminForm.password
                  }
                  onChange={(event) =>
                    setAdminForm(
                      (previous) => ({
                        ...previous,
                        password:
                          event.target
                            .value,
                      })
                    )
                  }
                  placeholder="Minimum 6 characters"
                  minLength={6}
                  required
                />
              </label>
            )}

            <label>
              Admin Role

              <select
                value={adminForm.role_id}
                disabled={
                  rolesLoading ||
                  (isEdit &&
                    selectedAdmin
                      ?.can_change_role ===
                      false)
                }
                onChange={(event) =>
                  setAdminForm(
                    (previous) => ({
                      ...previous,
                      role_id:
                        event.target.value,
                    })
                  )
                }
                required
              >
                <option value="">
                  {rolesLoading
                    ? "Loading roles..."
                    : "Select admin role"}
                </option>

                {roles.map((role) => (
                  <option
                    key={role.role_id}
                    value={role.role_id}
                  >
                    {role.role_name}
                  </option>
                ))}
              </select>

              {isEdit &&
                selectedAdmin
                  ?.can_change_role ===
                  false && (
                  <small className="text-muted">
                    The Super Admin role
                    cannot be changed.
                  </small>
                )}
            </label>

            <label>
              Status

              <select
                value={adminForm.status}
                disabled={
                  isEdit &&
                  selectedAdmin
                    ?.is_super_admin
                }
                onChange={(event) =>
                  setAdminForm(
                    (previous) => ({
                      ...previous,
                      status:
                        event.target.value,
                    })
                  )
                }
              >
                <option value="active">
                  Active
                </option>
                <option value="inactive">
                  Inactive
                </option>
                <option value="pending">
                  Pending
                </option>
                <option value="blocked">
                  Blocked
                </option>
              </select>
            </label>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={
                  closeFormSidebar
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-action"
                disabled={
                  saving ||
                  rolesLoading
                }
              >
                {saving
                  ? "Saving..."
                  : isEdit
                    ? "Update Admin"
                    : "Create Admin"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>

      {/* CHANGE PASSWORD */}

      <Sidebar
        visible={passwordSidebarOpen}
        position="right"
        onHide={() =>
          setPasswordSidebarOpen(false)
        }
        className="premium-user-sidebar compact-password-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>Security</p>
            <h2>Update Password</h2>
          </div>

          <form
            className="premium-sidebar-form"
            onSubmit={
              submitPasswordChange
            }
          >
            <label>
              New Password

              <input
                type="password"
                value={
                  passwordForm.password
                }
                onChange={(event) =>
                  setPasswordForm(
                    (previous) => ({
                      ...previous,
                      password:
                        event.target.value,
                    })
                  )
                }
                placeholder="Minimum 6 characters"
              />
            </label>

            <label>
              Confirm Password

              <input
                type="password"
                value={
                  passwordForm.confirmPassword
                }
                onChange={(event) =>
                  setPasswordForm(
                    (previous) => ({
                      ...previous,
                      confirmPassword:
                        event.target.value,
                    })
                  )
                }
                placeholder="Re-enter password"
              />
            </label>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() =>
                  setPasswordSidebarOpen(
                    false
                  )
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-action"
                disabled={
                  changingPassword
                }
              >
                {changingPassword
                  ? "Updating..."
                  : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>
    </section>
  );
};

export default Users;
